import React, { useEffect, useRef, useState } from 'react';
import './MissionConsole.css';

/**
 * Mission Console — operational ISR rig.
 *
 * Left:    Instrument cluster (ADI, airspeed tape, altimeter tape, HSI, VSI)
 * Centre:  Mission map with a 4-phase ISR story loop
 *            TRANSIT → SEARCH (lawnmower) → DETECT (objects classified) → TRACK (loiter)
 * Right:   Sensor fusion panel — SDR-fed multi-sensor (ESM, ADS-B, AIS,
 *          EO/IR, SAR Radar, DF, Link-16) feeding a single fusion confidence
 * Background: a real Particle Swarm Optimisation canvas converging on a
 *             moving optimum — same family of code that runs the PSO PID
 *             tuner in the dissertation work.
 */

/* ──────────────────────────── Instruments ──────────────────────────── */

function ADI({ pitch, roll }) {
  const horizonY = 80 + pitch * 1.6;
  return (
    <svg viewBox="0 0 160 160" className="mc-adi" aria-label="attitude indicator">
      <defs>
        <clipPath id="adi-clip"><circle cx="80" cy="80" r="74" /></clipPath>
      </defs>
      <g clipPath="url(#adi-clip)" transform={`rotate(${-roll} 80 80)`}>
        <rect x="-80" y={horizonY - 160} width="320" height="160" fill="#3a6da3" />
        <rect x="-80" y={horizonY}       width="320" height="320" fill="#6b4a2a" />
        <line x1="-80" y1={horizonY} x2="240" y2={horizonY} stroke="#fff" strokeWidth="1.8" />
        {[-30, -20, -10, 10, 20, 30].map((p) => (
          <g key={p}>
            <line x1={80 - (p % 20 === 0 ? 22 : 12)} y1={horizonY + p * 1.6}
                  x2={80 + (p % 20 === 0 ? 22 : 12)} y2={horizonY + p * 1.6}
                  stroke="#fff" strokeWidth="0.9" />
            {p % 10 === 0 && (
              <text x={80 - (p % 20 === 0 ? 26 : 16)} y={horizonY + p * 1.6 + 2.6}
                    fill="#fff" fontSize="6" textAnchor="end">{Math.abs(p)}</text>
            )}
          </g>
        ))}
      </g>
      <line x1="50" y1="80" x2="68" y2="80" stroke="#ffd24a" strokeWidth="3" />
      <line x1="92" y1="80" x2="110" y2="80" stroke="#ffd24a" strokeWidth="3" />
      <circle cx="80" cy="80" r="2.2" fill="#ffd24a" />
      <circle cx="80" cy="80" r="74" fill="none" stroke="#0b0f18" strokeWidth="3" />
      {[-60, -30, 0, 30, 60].map((a) => {
        const rad = (a - 90) * Math.PI / 180;
        return (
          <line key={a}
            x1={80 + 74 * Math.cos(rad)} y1={80 + 74 * Math.sin(rad)}
            x2={80 + 66 * Math.cos(rad)} y2={80 + 66 * Math.sin(rad)}
            stroke="#fff" strokeWidth="1" />
        );
      })}
      <polygon points="80,6 76,14 84,14" fill="#ffd24a" />
    </svg>
  );
}

function Tape({ value, unit, label, step = 10, range = 60 }) {
  const ticks = [];
  const base = Math.round(value / step) * step;
  for (let i = -range; i <= range; i += step / 2) {
    const v = base + i;
    if (v < 0) continue;
    const y = 80 - (v - value) * 3;
    if (y < 4 || y > 156) continue;
    const major = v % step === 0;
    ticks.push(
      <g key={v}>
        <line x1={major ? 8 : 14} y1={y} x2="26" y2={y} stroke="#9bb4d4" strokeWidth={major ? 1 : 0.5} />
        {major && (
          <text x="30" y={y + 2.5} fill="#c6d6ec" fontSize="8" fontFamily="JetBrains Mono, monospace">{v}</text>
        )}
      </g>
    );
  }
  return (
    <div className="mc-tape">
      <div className="mc-tape__label">{label}</div>
      <svg viewBox="0 0 70 160" className="mc-tape__svg">
        <rect x="0" y="0" width="70" height="160" fill="#0b101a" />
        {ticks}
        <rect x="0" y="68" width="70" height="24" fill="#1a2438" stroke="#ffd24a" strokeWidth="1" />
        <text x="35" y="85" fill="#ffd24a" fontSize="12" textAnchor="middle"
              fontFamily="JetBrains Mono, monospace" fontWeight="700">
          {Math.round(value)}
        </text>
      </svg>
      <div className="mc-tape__unit">{unit}</div>
    </div>
  );
}

function HSI({ heading, bearing }) {
  const ticks = Array.from({ length: 36 }, (_, i) => i * 10);
  return (
    <svg viewBox="0 0 160 160" className="mc-hsi" aria-label="heading">
      <circle cx="80" cy="80" r="72" fill="#0b101a" stroke="#1a2438" strokeWidth="2" />
      <g transform={`rotate(${-heading} 80 80)`}>
        {ticks.map((deg) => {
          const rad = (deg - 90) * Math.PI / 180;
          const major = deg % 30 === 0;
          const r1 = 70;
          const r2 = major ? 60 : 66;
          return (
            <g key={deg}>
              <line
                x1={80 + r1 * Math.cos(rad)} y1={80 + r1 * Math.sin(rad)}
                x2={80 + r2 * Math.cos(rad)} y2={80 + r2 * Math.sin(rad)}
                stroke="#9bb4d4" strokeWidth={major ? 1.2 : 0.6}
              />
              {major && (
                <text
                  x={80 + 52 * Math.cos(rad)} y={80 + 52 * Math.sin(rad) + 3}
                  fill="#c6d6ec" fontSize="8" textAnchor="middle"
                  fontFamily="JetBrains Mono, monospace"
                  transform={`rotate(${deg} ${80 + 52 * Math.cos(rad)} ${80 + 52 * Math.sin(rad)})`}
                >
                  {deg === 0 ? 'N' : deg === 90 ? 'E' : deg === 180 ? 'S' : deg === 270 ? 'W' : (deg / 10).toString().padStart(2, '0')}
                </text>
              )}
            </g>
          );
        })}
        <g transform={`rotate(${bearing} 80 80)`}>
          <line x1="80" y1="20" x2="80" y2="140" stroke="#5b7fff" strokeWidth="1.2" strokeDasharray="3 3" />
          <polygon points="80,18 76,28 84,28" fill="#5b7fff" />
        </g>
      </g>
      <polygon points="80,6 74,18 86,18" fill="#ffd24a" />
      <circle cx="80" cy="80" r="3" fill="#ffd24a" />
      <text x="80" y="156" fill="#9bb4d4" fontSize="9" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace">
        HDG {Math.round(heading).toString().padStart(3, '0')}°
      </text>
    </svg>
  );
}

function VSI({ vs }) {
  const clamped = Math.max(-2000, Math.min(2000, vs));
  const angle = (clamped / 2000) * 90;
  return (
    <svg viewBox="0 0 90 90" className="mc-vsi" aria-label="vertical speed">
      <circle cx="45" cy="45" r="42" fill="#0b101a" stroke="#1a2438" strokeWidth="1.5" />
      {[-2, -1, 0, 1, 2].map((v) => {
        const a = (v / 2) * 90;
        const rad = (a - 180) * Math.PI / 180;
        return (
          <g key={v}>
            <line
              x1={45 + 40 * Math.cos(rad)} y1={45 + 40 * Math.sin(rad)}
              x2={45 + 33 * Math.cos(rad)} y2={45 + 33 * Math.sin(rad)}
              stroke="#9bb4d4" strokeWidth="1"
            />
            <text
              x={45 + 26 * Math.cos(rad)} y={45 + 26 * Math.sin(rad) + 2}
              fill="#c6d6ec" fontSize="6" textAnchor="middle"
              fontFamily="JetBrains Mono, monospace"
            >{Math.abs(v)}</text>
          </g>
        );
      })}
      <line
        x1="45" y1="45"
        x2={45 + 32 * Math.cos((angle - 180) * Math.PI / 180)}
        y2={45 + 32 * Math.sin((angle - 180) * Math.PI / 180)}
        stroke="#ffd24a" strokeWidth="2"
      />
      <circle cx="45" cy="45" r="2.2" fill="#ffd24a" />
      <text x="45" y="86" fill="#9bb4d4" fontSize="6" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace">VSI ×1000 fpm</text>
    </svg>
  );
}

/* ──────────────────────────── Mission map + story ──────────────────── */

const PHASES = [
  { id: 'TRANSIT', label: 'INGRESS TRANSIT',  detail: 'Cruise to AO. Sensor array warm-up. Link-16 handshake.' },
  { id: 'SEARCH',  label: 'AREA SEARCH',      detail: 'Executing lawnmower pattern. EO/IR scanning surface track.' },
  { id: 'DETECT',  label: 'CONTACT CLASSIFY', detail: 'YOLO-mini on Jetson Orin. Cross-cuing radar + ESM bearing.' },
  { id: 'TRACK',   label: 'LOITER + TRACK',   detail: 'Holding orbit on contact. Track quality climbing. Replan queued.' },
];

const POIs = [
  { id: 'C-01', x: 162, y: 78,  type: 'CIV', label: 'CIV — fishing vsl',     p: 0.32 },
  { id: 'U-01', x: 248, y: 142, type: 'UNK', label: 'UNK — surface contact', p: 0.46 },
  { id: 'U-02', x: 308, y: 96,  type: 'UNK', label: 'UNK — air contact',     p: 0.58 },
  { id: 'T-01', x: 274, y: 188, type: 'TGT', label: 'TGT — pri 1 surface',   p: 0.72 },
];

function MissionMap({ progress, phaseIdx, drone, trail }) {
  const searchBox = { x: 130, y: 60, w: 220, h: 170 };
  const lanes = (() => {
    const out = [];
    const lanesN = 5;
    const dy = searchBox.h / (lanesN - 1);
    for (let i = 0; i < lanesN; i++) {
      const y = searchBox.y + i * dy;
      const x1 = i % 2 === 0 ? searchBox.x : searchBox.x + searchBox.w;
      const x2 = i % 2 === 0 ? searchBox.x + searchBox.w : searchBox.x;
      out.push({ x1, y1: y, x2, y2: y });
    }
    return out;
  })();

  const visiblePOIs = POIs.filter((poi) => progress >= poi.p);
  const target = POIs.find((p) => p.id === 'T-01');

  return (
    <svg viewBox="0 0 420 280" className="mc-map" aria-label="mission map">
      <defs>
        <pattern id="mc-grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#16203a" strokeWidth="0.5" />
        </pattern>
        <pattern id="mc-grid-major" width="100" height="100" patternUnits="userSpaceOnUse">
          <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#1f2e52" strokeWidth="0.8" />
        </pattern>
      </defs>
      <rect width="420" height="280" fill="#070b14" />
      <rect width="420" height="280" fill="url(#mc-grid)" />
      <rect width="420" height="280" fill="url(#mc-grid-major)" />

      <rect x={searchBox.x} y={searchBox.y} width={searchBox.w} height={searchBox.h}
            fill="none" stroke="#5b7fff" strokeWidth="0.8" strokeDasharray="6 4" opacity="0.6" />
      <text x={searchBox.x + 4} y={searchBox.y - 4} fill="#5b7fff" fontSize="8"
            fontFamily="JetBrains Mono, monospace" opacity="0.85">AOI · ALPHA-7</text>

      <circle cx="62" cy="220" r="34" fill="none" stroke="#ff6a55" strokeWidth="0.6" strokeDasharray="3 3" opacity="0.7" />
      <text x="62" y="222" fill="#ff6a55" fontSize="6" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace" opacity="0.85">RESTRICTED</text>

      {lanes.map((l, i) => (
        <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
              stroke="#4ad99c" strokeWidth="0.6"
              opacity={phaseIdx >= 1 ? 0.55 : 0.18} strokeDasharray="2 3" />
      ))}

      {trail.length > 1 && (
        <polyline
          fill="none"
          stroke="#ffd24a"
          strokeWidth="1.2"
          strokeOpacity="0.85"
          points={trail.map((p) => `${p.x},${p.y}`).join(' ')}
        />
      )}

      {visiblePOIs.map((poi) => {
        const color = poi.type === 'TGT' ? '#ff6a55' : poi.type === 'UNK' ? '#ffd24a' : '#4ad99c';
        return (
          <g key={poi.id} className="mc-poi">
            <circle cx={poi.x} cy={poi.y} r="5" fill="none" stroke={color} strokeWidth="1" />
            <circle cx={poi.x} cy={poi.y} r="1.6" fill={color} />
            <text x={poi.x + 8} y={poi.y - 6} fill={color} fontSize="6.5"
                  fontFamily="JetBrains Mono, monospace">{poi.label}</text>
            <text x={poi.x + 8} y={poi.y + 5} fill={color} fontSize="5.5" opacity="0.7"
                  fontFamily="JetBrains Mono, monospace">{poi.id}</text>
          </g>
        );
      })}

      {phaseIdx === 3 && target && (
        <circle cx={target.x} cy={target.y} r="36"
                fill="none" stroke="#ff6a55" strokeWidth="0.8" strokeDasharray="2 4" opacity="0.6" />
      )}

      <g transform={`translate(${drone.x}, ${drone.y}) rotate(${drone.hdg})`}>
        <circle r="9" fill="rgba(91,127,255,0.15)" />
        <polygon points="0,-7 5,5 0,2 -5,5" fill="#5b7fff" stroke="#fff" strokeWidth="0.6" />
      </g>

      <g fontFamily="JetBrains Mono, monospace" fontSize="7" fill="#c6d6ec">
        <text x="8" y="14">SECTOR 51N 04W</text>
        <text x="8" y="24" opacity="0.6">SCALE 1:25k</text>
        <text x="412" y="14" textAnchor="end">{PHASES[phaseIdx].id}</text>
        <text x="412" y="24" textAnchor="end" opacity="0.65">
          TRK {(progress * 100).toFixed(0)}%
        </text>
      </g>
    </svg>
  );
}

/* ──────────────────────────── Sensor fusion panel ───────────────────── */

const SENSORS = [
  { id: 'ESM',     band: 'L–Ku',    note: 'passive emitter detect' },
  { id: 'ADS-B',   band: '1090MHz', note: 'civil air picture' },
  { id: 'AIS',     band: '162MHz',  note: 'surface vessel ID' },
  { id: 'EO/IR',   band: '8–14µm',  note: 'thermal track + classify' },
  { id: 'SAR',     band: 'X-band',  note: 'SDR-formed synthetic aperture' },
  { id: 'DF',      band: 'HF–UHF',  note: 'direction finding bearing' },
  { id: 'LINK-16', band: 'TDMA',    note: 'tactical data network' },
];

function FusionPanel({ tick, phaseIdx, fusionConf }) {
  const rows = SENSORS.map((s, i) => {
    const phaseWeight = (() => {
      switch (s.id) {
        case 'ESM':     return [0.4, 0.6, 0.9, 0.85][phaseIdx];
        case 'ADS-B':   return [0.7, 0.7, 0.5, 0.4][phaseIdx];
        case 'AIS':     return [0.5, 0.6, 0.7, 0.8][phaseIdx];
        case 'EO/IR':   return [0.2, 0.55, 0.9, 0.95][phaseIdx];
        case 'SAR':     return [0.3, 0.6, 0.85, 0.9][phaseIdx];
        case 'DF':      return [0.4, 0.5, 0.75, 0.8][phaseIdx];
        case 'LINK-16': return [0.7, 0.7, 0.7, 0.7][phaseIdx];
        default: return 0.5;
      }
    })();
    const wobble = 0.08 * Math.sin(tick * 0.04 + i);
    const sig = Math.max(0, Math.min(1, phaseWeight + wobble));
    return { ...s, sig };
  });

  return (
    <div className="mc-fusion">
      <div className="mc-fusion__hdr">
        <span className="mc-fusion__title">Sensor Fusion · SDR Gimbal Payload</span>
        <span className="mc-fusion__conf">
          fusion conf <strong>{(fusionConf * 100).toFixed(0)}%</strong>
        </span>
      </div>
      <ul className="mc-fusion__list">
        {rows.map((r) => (
          <li key={r.id} className="mc-fusion__row">
            <span className="mc-fusion__id">{r.id}</span>
            <span className="mc-fusion__band">{r.band}</span>
            <div className="mc-fusion__bar">
              <div className="mc-fusion__bar-fill" style={{ width: `${r.sig * 100}%` }} />
            </div>
            <span className="mc-fusion__note">{r.note}</span>
          </li>
        ))}
      </ul>
      <div className="mc-fusion__foot">
        single SDR front-end · multi-channel digital back-end · sensor-fused track
        the way an ASW aircraft works, scaled down onto the drone gimbal
      </div>
    </div>
  );
}

/* ──────────────────────────── PSO background canvas ─────────────────── */

function PSOBackground() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let gbest = { x: 0, y: 0, score: -Infinity };
    let optimum = { x: 0, y: 0 };
    let t = 0;

    const fitness = (x, y, w, h) => {
      const dx = x - optimum.x;
      const dy = y - optimum.y;
      return -Math.sqrt(dx * dx + dy * dy) / Math.hypot(w, h);
    };

    const seed = (w, h) => {
      optimum = { x: w / 2, y: h / 2 };
      particles = Array.from({ length: 60 }, () => {
        const x = Math.random() * w;
        const y = Math.random() * h;
        return {
          x, y,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          pbest: { x, y, score: -Infinity },
        };
      });
      gbest = { x: w / 2, y: h / 2, score: -Infinity };
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed(rect.width, rect.height);
    };

    const tick = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      t += 1;
      optimum.x = w * (0.5 + 0.32 * Math.cos(t * 0.004));
      optimum.y = h * (0.5 + 0.32 * Math.sin(t * 0.0033));

      const w1 = 0.72;
      const c1 = 0.6;
      const c2 = 0.9;

      ctx.fillStyle = 'rgba(7,10,16,0.18)';
      ctx.fillRect(0, 0, w, h);

      for (const p of particles) {
        const r1 = Math.random();
        const r2 = Math.random();
        p.vx = w1 * p.vx + c1 * r1 * (p.pbest.x - p.x) + c2 * r2 * (gbest.x - p.x);
        p.vy = w1 * p.vy + c1 * r1 * (p.pbest.y - p.y) + c2 * r2 * (gbest.y - p.y);
        const max = 1.8;
        const sp = Math.hypot(p.vx, p.vy);
        if (sp > max) { p.vx *= max / sp; p.vy *= max / sp; }
        p.x += p.vx;
        p.y += p.vy;
        const s = fitness(p.x, p.y, w, h);
        if (s > p.pbest.score) p.pbest = { x: p.x, y: p.y, score: s };
        if (s > gbest.score)   gbest   = { x: p.x, y: p.y, score: s };

        ctx.fillStyle = 'rgba(120, 170, 255, 0.5)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.3, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = 'rgba(120, 170, 255, 0.16)';
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x - p.vx * 6, p.y - p.vy * 6);
        ctx.stroke();
      }

      ctx.strokeStyle = 'rgba(255, 210, 74, 0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(gbest.x, gbest.y, 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(74, 217, 156, 0.3)';
      ctx.beginPath();
      ctx.moveTo(optimum.x - 6, optimum.y);
      ctx.lineTo(optimum.x + 6, optimum.y);
      ctx.moveTo(optimum.x, optimum.y - 6);
      ctx.lineTo(optimum.x, optimum.y + 6);
      ctx.stroke();

      raf = requestAnimationFrame(tick);
    };

    resize();
    raf = requestAnimationFrame(tick);
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <canvas ref={ref} className="mc-pso" aria-hidden="true" />;
}

/* ─── Deterministic drone path helper — reused for look-ahead tangent ── */

function getDronePos(prog) {
  const xs = 130, ys = 60, ws = 220, lanesN = 5;
  const dyLane = 170 / (lanesN - 1);
  const orbitCx = 274, orbitCy = 188, orbitR = 36;
  const orbitStartAng = Math.PI;
  const pi = prog < 0.18 ? 0 : prog < 0.50 ? 1 : prog < 0.78 ? 2 : 3;

  if (pi === 0) {
    const k = prog / 0.18;
    return { x: 30 + (xs - 30) * k, y: 245 + (ys - 245) * k };
  }
  if (pi === 1) {
    const k = (prog - 0.18) / (0.50 - 0.18);
    const total = (lanesN - 1) * 2;
    const seg = Math.min(total - 1e-5, k * total);
    const idx = Math.floor(seg);
    const segK = seg - idx;
    if (idx % 2 === 0) {
      const laneI = idx / 2;
      const y = ys + laneI * dyLane;
      const ltr = laneI % 2 === 0;
      return { x: ltr ? xs + ws * segK : xs + ws * (1 - segK), y };
    }
    const fromLane = (idx - 1) / 2;
    const y0 = ys + fromLane * dyLane;
    const y1 = ys + (fromLane + 1) * dyLane;
    const xEnd = fromLane % 2 === 0 ? xs + ws : xs;
    return { x: xEnd, y: y0 + (y1 - y0) * segK };
  }
  if (pi === 2) {
    const k = (prog - 0.50) / (0.78 - 0.50);
    const ex = xs, ey = ys + (lanesN - 1) * dyLane;
    const ox = orbitCx + orbitR * Math.cos(orbitStartAng);
    const oy = orbitCy + orbitR * Math.sin(orbitStartAng);
    return { x: ex + (ox - ex) * k, y: ey + (oy - ey) * k };
  }
  const k = (prog - 0.78) / (1 - 0.78);
  const ang = orbitStartAng + k * Math.PI * 4;
  return { x: orbitCx + orbitR * Math.cos(ang), y: orbitCy + orbitR * Math.sin(ang) };
}

/* ──────────────────────────── Main component ────────────────────────── */

const MissionConsole = () => {
  const [state, setState] = useState({
    pitch: 0, roll: 0, alt: 412, spd: 24, hdg: 78, vs: 320,
    progress: 0, phaseIdx: 0, fusionConf: 0.55,
    drone: { x: 80, y: 230, hdg: 60 },
    trail: [{ x: 80, y: 230 }],
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const loop = (now) => {
      const t = (now - start) / 1000;

      const loopDur = 36;
      const progress = (t % loopDur) / loopDur;
      const phaseIdx =
        progress < 0.18 ? 0 :
        progress < 0.50 ? 1 :
        progress < 0.78 ? 2 : 3;

      const pitch = 2 * Math.sin(t * 0.6) + (phaseIdx === 0 ? 4 : phaseIdx === 3 ? -1 : 0);
      // roll is computed from actual turn rate below — not hard-wired to phase
      const spdTarget =
        phaseIdx === 0 ? 28 + 1.5 * Math.sin(t * 0.4) :
        phaseIdx === 1 ? 22 + 1.5 * Math.sin(t * 0.7) :
        phaseIdx === 2 ? 19 + 1.5 * Math.sin(t * 0.9) :
                         16 + 1.5 * Math.sin(t * 1.1);
      // Smooth speed so phase-boundary steps don't snap the tape
      const spd = stateRef.current.spd + (spdTarget - stateRef.current.spd) * 0.04;
      // Low-pass filter alt and vs so phase transitions never snap the tapes
      const altTarget = 380 + 40 * Math.sin(t * 0.18) + (phaseIdx === 3 ? -15 : 0);
      const alt = stateRef.current.alt + (altTarget - stateRef.current.alt) * 0.04;
      const vsTarget = 280 * Math.sin(t * 0.2) + (phaseIdx === 3 ? -60 : 0);
      const vs  = stateRef.current.vs + (vsTarget - stateRef.current.vs) * 0.06;

      // Drone position — delegate to module-level getDronePos (also used for look-ahead)
      const dronePos = getDronePos(progress);

      // ── Heading: look-ahead tangent — anticipates corners, no frame-delta snap ──
      // Evaluate path 0.006 of a loop ahead so the aircraft starts banking
      // into turns before reaching the corner rather than snapping direction
      // at the waypoint.  Look-ahead distance ≈ 0.006 × 36 s = 0.22 s.
      const laP = Math.min(0.9999, progress + 0.006);
      const ahead = getDronePos(laP);
      const ddx = ahead.x - dronePos.x;
      const ddy = ahead.y - dronePos.y;
      // Tangent heading from the look-ahead vector (always has non-zero magnitude
      // except at the very end of a loop, where we hold the last heading)
      const tangentMag = Math.hypot(ddx, ddy);
      const geoHdg = tangentMag > 0.01
        ? (Math.atan2(ddx, -ddy) * 180 / Math.PI + 360) % 360
        : stateRef.current.hdg;
      // Limit turn rate to 2.5°/frame — smooth, realistic banking
      const prevHdg = stateRef.current.hdg;
      const dHdg = ((geoHdg - prevHdg + 540) % 360) - 180;
      const heading = (prevHdg + Math.sign(dHdg) * Math.min(Math.abs(dHdg), 2.5) + 360) % 360;
      // Roll: gentle lag (0.06) and moderate demand so the ADI doesn't snap
      const turnRate = ((heading - prevHdg + 540) % 360) - 180; // deg/frame
      const rollDemand = Math.max(-32, Math.min(32, turnRate * 9));
      const roll = stateRef.current.roll + (rollDemand - stateRef.current.roll) * 0.06;
      // ─────────────────────────────────────────────────────────────────────

      let trail = stateRef.current.trail;
      const lastTrail = trail[trail.length - 1];
      if (!lastTrail || Math.hypot(lastTrail.x - dronePos.x, lastTrail.y - dronePos.y) > 4) {
        trail = [...trail, dronePos].slice(-90);
      }
      if (progress < 0.02) trail = [dronePos];

      const fusionConf =
        phaseIdx === 0 ? 0.45 :
        phaseIdx === 1 ? 0.62 :
        phaseIdx === 2 ? 0.78 + 0.06 * Math.sin(t * 0.9) :
                         0.92 + 0.04 * Math.sin(t * 1.1);

      setState({
        pitch, roll, spd, alt, hdg: heading, vs,
        progress, phaseIdx, fusionConf,
        drone: { x: dronePos.x, y: dronePos.y, hdg: heading },
        trail,
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const phase = PHASES[state.phaseIdx];
  const bearing = (() => {
    const tgt = { x: 274, y: 188 };
    const dx = tgt.x - state.drone.x;
    const dy = tgt.y - state.drone.y;
    return (Math.atan2(dx, -dy) * 180 / Math.PI - state.hdg + 360) % 360;
  })();

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 100);
    return () => clearInterval(i);
  }, []);

  return (
    <section
      id="console"
      className="mission-console"
      data-section-theme="console"
      style={{
        '--nav-bg': 'rgba(7,10,16,0.7)',
        '--nav-text': '#f5f2ec',
        '--nav-accent': '#5b7fff',
        '--nav-border': 'rgba(245,242,236,0.08)',
        '--section-bg': '#070a10',
      }}
    >
      <PSOBackground />

      <div className="mission-console__inner">
        <header className="mc-header">
          <div className="mc-header__l">
            <span className="mc-eyebrow">LIVE · MISSION DECISION ENGINE</span>
            <h2 className="mc-title">Mission Console</h2>
            <p className="mc-sub">
              An ISR mission playing out the way the on-board firmware runs it — flight,
              search, classify, track, replan. Particle Swarm Optimisation chases a moving
              optimum in the background, same family of code that tunes the autopilot.
            </p>
          </div>
          <div className="mc-header__r">
            <div className={`mc-phase mc-phase--${phase.id.toLowerCase()}`}>
              <span className="mc-phase__pill">{phase.id}</span>
              <span className="mc-phase__label">{phase.label}</span>
              <span className="mc-phase__detail">{phase.detail}</span>
            </div>
          </div>
        </header>

        <div className="mc-grid">
          <div className="mc-panel mc-panel--instr">
            <div className="mc-panel__hdr">
              <span>INSTRUMENT CLUSTER · EFIS</span>
              <span className="mc-blink">●</span>
            </div>

            {/* Mode annunciator strip */}
            <div className="mc-annunciator">
              <span className={`mc-ann mc-ann--${phase.id.toLowerCase()}`}>
                {phase.id === 'TRANSIT' ? 'NAV' : phase.id === 'SEARCH' ? 'LMOW' : phase.id === 'DETECT' ? 'SRCH' : 'TRCK'}
              </span>
              <span className="mc-ann mc-ann--on">AUTPLT</span>
              <span className="mc-ann mc-ann--on">SENSOR</span>
              <span className="mc-ann mc-ann--on">LINK</span>
            </div>

            {/* Primary row: ASI | ATT | ALT */}
            <div className="mc-instr">
              <div className="mc-instr__cell">
                <span className="mc-instr__cell-id">ASI</span>
                <Tape value={state.spd} unit="kts" label="" step={10} range={50} />
              </div>
              <div className="mc-instr__cell">
                <span className="mc-instr__cell-id">ATT</span>
                <ADI pitch={state.pitch} roll={state.roll} />
              </div>
              <div className="mc-instr__cell">
                <span className="mc-instr__cell-id">ALT</span>
                <Tape value={state.alt} unit="ft" label="" step={50} range={250} />
              </div>
            </div>

            <div className="mc-efis-bar" />

            {/* Navigation row: HSI | FMS data | VSI */}
            <div className="mc-instr mc-instr--row2">
              <div className="mc-instr__cell">
                <span className="mc-instr__cell-id">HSI</span>
                <HSI heading={state.hdg} bearing={bearing} />
              </div>
              <div className="mc-instr__numeric">
                <div className="mc-num"><span>GND SPD</span><strong>{state.spd.toFixed(0)} kts</strong></div>
                <div className="mc-num"><span>ALT MSL</span><strong>{state.alt.toFixed(0)} ft</strong></div>
                <div className="mc-num"><span>HDG</span><strong>{state.hdg.toFixed(0).padStart(3,'0')}°</strong></div>
                <div className="mc-num"><span>BRG TGT</span><strong>{bearing.toFixed(0).padStart(3,'0')}°</strong></div>
                <div className="mc-num"><span>FUEL</span><strong>{(78 - state.progress * 22).toFixed(0)}%</strong></div>
                <div className="mc-num"><span>LINK</span><strong style={{color:'#4ad99c'}}>NOM</strong></div>
              </div>
              <div className="mc-instr__cell">
                <span className="mc-instr__cell-id">VSI</span>
                <VSI vs={state.vs} />
              </div>
            </div>
          </div>

          <div className="mc-panel mc-panel--map">
            <div className="mc-panel__hdr">
              <span>TACTICAL DISPLAY · AO ALPHA-7</span>
              <span>{new Date().toISOString().slice(11, 19)}Z</span>
            </div>
            <MissionMap
              progress={state.progress}
              phaseIdx={state.phaseIdx}
              drone={state.drone}
              trail={state.trail}
            />
            <div className="mc-mission-log">
              <span className="mc-log-line">
                <em>{phase.id}</em> · {phase.detail}
              </span>
              <span className="mc-log-line mc-log-line--dim">
                track id <code>UAV-OVR-01</code> · classifier yolov8n-int8 · re-plan window 4.0s
              </span>
            </div>
          </div>

          <div className="mc-panel mc-panel--fusion">
            <FusionPanel tick={tick} phaseIdx={state.phaseIdx} fusionConf={state.fusionConf} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionConsole;
